import { PaymentMethod } from "../../../../common/enums";
import { isUSA } from "../../../../common/utils";
import { type SubscriptionAddressFullFragment } from "../../../../graphql/supergraph.index";
import { AddressInputType } from "../../../SharedAddressInput/constants";
import { DetailedCheckPaymentPrefObjectType } from "../../common/types";
import { getIdFromCountry, getNameFromCountry, mapCountryToValueSafely } from "../../mappers";
import { CheckPaymentPrefInputFields, type CheckPaymentPrefInputType } from "./constants";

export const mapFormikAddressToGQLObject = (
  address: AddressInputType,
): SubscriptionAddressFullFragment => {
  const isUSACountry = isUSA(getNameFromCountry(address?.country));

  return {
    city: address?.city?.trim() || null,
    country: address?.country
      ? {
          id: getIdFromCountry(address?.country),
          name: getNameFromCountry(address?.country),
          alpha2code: null,
          code: null,
        }
      : null,
    postalCode: address?.zip?.trim() || null,
    region: !isUSACountry ? address?.province?.trim() || null : null,
    state: isUSACountry
      ? {
          __typename: "StateObjectType",
          // We are over-loading this object and should narrow it.
          code: address?.state?.trim() ?? "",
          // We are over-loading this object and should narrow it.
          id: address?.state?.trim() ?? "",
          name: "",
        }
      : null,
    street: address?.streetLine1?.trim() || null,
    street2: address?.streetLine2?.trim() || null,
    street3: null,
  };
};

export const getAddressSummary = (address: SubscriptionAddressFullFragment): string => {
  const addressStreet = [address.street, address.street2, address.street3, address.city]
    .filter((a) => Boolean(a))
    .join(" ");

  const addressRegion = [
    address.state?.name,
    address.postalCode,
    address.region,
    address.country?.name,
  ]
    .filter((a) => Boolean(a))
    .join(" ");

  return `${addressStreet}, ${addressRegion}`;
};

export const getBankInfoRedacted = (
  payeeName: string,
  address: SubscriptionAddressFullFragment | null,
): string => {
  return address ? `${payeeName}; ${getAddressSummary(address)}` : payeeName;
};

export const mapFormStateToContextState = (
  values: CheckPaymentPrefInputType,
): DetailedCheckPaymentPrefObjectType => {
  const payeeName = values[CheckPaymentPrefInputFields.PayeeName].trim();

  const address = !values[CheckPaymentPrefInputFields.Address].sameAsMailingAddress
    ? mapFormikAddressToGQLObject(values?.address)
    : null;

  return {
    __typename: "CheckPaymentPrefObjectType",
    id: null,
    address,
    memo: values[CheckPaymentPrefInputFields.Memo]?.trim() ?? "",
    payeeName,
    useAccountMailingAddress:
      values[CheckPaymentPrefInputFields.Address].sameAsMailingAddress ?? false,
    naturalPersonDateOfBirth: null,
    naturalPersonEmailAddress: null,
    naturalPersonFirstName: null,
    naturalPersonLastName: null,
    naturalPersonMiddleName: null,
    paymentMethod: PaymentMethod.Check,
    paymentPrefId: null,
    bankCountry: null,
    bankName: null,
    accountHolderType: null,
    bankInfoRedacted: getBankInfoRedacted(payeeName, address),
  };
};

const mapGQLAddressToFormikState = (
  gqlAddress?: SubscriptionAddressFullFragment | null,
  sameAsMailingAddress?: boolean | null,
): AddressInputType => {
  const countryName = gqlAddress?.country?.name;

  return {
    sameAsMailingAddress: sameAsMailingAddress ?? false,
    streetLine1: gqlAddress?.street ?? "",
    streetLine2: gqlAddress?.street2 ?? "",
    city: gqlAddress?.city ?? "",
    zip: gqlAddress?.postalCode ?? "",
    state: countryName && isUSA(countryName) ? gqlAddress?.state?.code ?? "" : "",
    province: countryName && !isUSA(countryName) ? gqlAddress?.region ?? "" : "",
    country: mapCountryToValueSafely(gqlAddress?.country?.id, countryName),
  };
};

export const mapContextStateToFormState = (
  input?: DetailedCheckPaymentPrefObjectType,
): CheckPaymentPrefInputType => {
  return {
    address: mapGQLAddressToFormikState(input?.address, input?.useAccountMailingAddress),
    payeeName: input?.payeeName ?? "",
    memo: input?.memo ?? "",
  };
};
